import type { ArrayForm, ObjectForm } from "@pdfking/forms"
import type { FileName } from "@pdfking/structs"
import { NullQ } from "@pdfking/structs"
import { JSONEncode, Keys } from "@pdfking/tfunc"

import type { SidebarViewProps } from "#public"

// How many rows still name this file. The store is keyed by name, so several
// rows can share one entry — it only goes when the last of them does.
const reference_count = (
  files_node: ObjectForm<Record<string, unknown>> | undefined,
  name: string
): number => {
  const rows = (files_node?.results.file_key as ArrayForm<unknown> | undefined)?.results ?? []
  return rows.filter(row => (row.input as Record<string, unknown> | undefined)?.file_id === name)
    .length
}

export function DebugView({ files_node, state, store }: SidebarViewProps) {
  const names = Keys(store.files) as FileName[]
  return (
    <div className="flex flex-col gap-4 p-2">
      {/* What the file store actually holds, against what the form still
          references. `sync_files` drops an entry once no row names it, and this
          is where that can be watched: an entry sitting at 0 refs is a leak. */}
      <div className="flex flex-col gap-1">
        <div className="text-[10px] font-semibold text-muted-foreground uppercase">
          File store ({names.length})
        </div>
        {names.length ? (
          names.map(name => {
            const entry = store.files[name]
            const refs = reference_count(files_node, name)
            return (
              <div className="flex items-baseline justify-between gap-2 text-xs" key={name}>
                <span className="truncate" title={name}>
                  {name}
                </span>
                <span className="shrink-0 text-muted-foreground">
                  {entry.error
                    ? "error"
                    : NullQ(entry.page_count)
                      ? "loading"
                      : `${entry.page_count}p`}
                  {" · "}
                  <span className={refs ? undefined : "text-destructive"}>{refs} refs</span>
                </span>
              </div>
            )
          })
        ) : (
          <div className="text-xs text-muted-foreground">empty</div>
        )}
      </div>

      <pre className="overflow-auto px-2 text-xs break-all whitespace-pre-wrap">
        {JSONEncode(state, { indent: 2 })}
      </pre>
    </div>
  )
}
